/**
 * Utility functions for drag & drop reservation scheduling
 */

/**
 * Validate if a booking can be moved to a new date/room
 * @param {Object} booking - The booking to validate
 * @param {string} targetRoom - Target room number
 * @param {string} targetDate - Target date (YYYY-MM-DD)
 * @param {Object} scheduleData - Current schedule data
 * @param {string} currentMonth - Current month being viewed (YYYY-MM)
 * @returns {Object} { isValid: boolean, message: string }
 */
export const validateBookingMove = (booking, targetRoom, targetDate, scheduleData, currentMonth) => {
  if (!booking || !booking.id) {
    return { isValid: false, message: 'Invalid booking data' };
  }

  const targetDateObj = new Date(targetDate);
  const checkinDate = new Date(booking.checkin);
  const checkoutDate = new Date(booking.checkout);

  // Check if target date is within booking's stay duration
  const stayDuration = Math.ceil((checkoutDate - checkinDate) / (1000 * 60 * 60 * 24));
  
  if (stayDuration <= 0) {
    return { isValid: false, message: 'Invalid booking date range' };
  }

  // Check if target room exists in schedule
  if (!scheduleData.listNumberRoom || !scheduleData.listNumberRoom.includes(targetRoom)) {
    return { isValid: false, message: 'Target room does not exist' };
  }

  // Get the target room's schedule data
  const targetRoomData = scheduleData.listReservation?.[targetRoom];
  if (!targetRoomData) {
    return { isValid: false, message: 'Cannot access room schedule' };
  }

  // Calculate target date index
  const monthStart = new Date(currentMonth + '-01');
  const targetIndex = Math.ceil((targetDateObj - monthStart) / (1000 * 60 * 60 * 24));

  // Validate consecutive empty cells for the entire stay
  for (let i = 0; i < stayDuration && targetIndex + i < targetRoomData.length; i++) {
    const cellData = targetRoomData[targetIndex + i];
    if (cellData !== "" && cellData !== undefined) {
      return { 
        isValid: false, 
        message: `Room ${targetRoom} is not available for the entire stay period. Cell ${targetIndex + i + 1} is occupied.` 
      };
    }
  }

  return { isValid: true, message: 'Booking can be moved to this location' };
};

/**
 * Check if booking is within current month view
 * @param {Object} booking - Booking object
 * @param {string} currentMonth - Current month (YYYY-MM)
 * @returns {boolean}
 */
export const isBookingInCurrentMonth = (booking, currentMonth) => {
  if (!booking || !booking.checkin || !currentMonth) return false;

  const rawCheckin = String(booking.checkin).trim();
  const datePart = rawCheckin.split('@')[0].trim();
  const normalizedCurrentMonthMatch = String(currentMonth).trim().match(/(\d{4})-(\d{1,2})/);

  if (!normalizedCurrentMonthMatch) return false;

  const normalizedCurrentMonth = `${normalizedCurrentMonthMatch[1]}-${normalizedCurrentMonthMatch[2].padStart(2, '0')}`;

  let bookingMonth = '';

  // DD-MM-YYYY or D-M-YYYY
  let m = datePart.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m) {
    bookingMonth = `${m[3]}-${m[2].padStart(2, '0')}`;
  }

  // YYYY-MM-DD or YYYY-M-D
  if (!bookingMonth) {
    m = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) {
      bookingMonth = `${m[1]}-${m[2].padStart(2, '0')}`;
    }
  }

  // YYYY-MM only
  if (!bookingMonth) {
    m = datePart.match(/^(\d{4})-(\d{1,2})$/);
    if (m) {
      bookingMonth = `${m[1]}-${m[2].padStart(2, '0')}`;
    }
  }

  const matches = bookingMonth === normalizedCurrentMonth;
//   console.log('🔍 Month parsing:', {
//     rawCheckin,
//     datePart,
//     bookingMonth,
//     currentMonth,
//     normalizedCurrentMonth,
//     matches
//   });

  return matches;
};

/**
 * Create drag log entry
 * @param {Object} params - Log parameters
 * @returns {Object} Log entry
 */
export const createDragLog = ({
  bookingId,
  guestName,
  sourceRoom,
  targetRoom,
  sourceDate,
  targetDate,
  username,
  branchID,
  timestamp = new Date().toISOString(),
  status = 'pending' // pending, success, failed
}) => {
  return {
    bookingId,
    guestName,
    sourceRoom,
    targetRoom,
    sourceDate,
    targetDate,
    username,
    branchID,
    timestamp,
    status,
    action: 'drag_drop_reschedule'
  };
};

/**
 * Parse date string to get day number within month
 * @param {string} dateStr - Date string (YYYY-MM-DD or YYYY-MM-DD)
 * @param {string} monthStr - Month string (YYYY-MM)
 * @returns {number} Day index in month (1-based)
 */
export const getDateIndexInMonth = (dateStr, monthStr) => {
  const date = new Date(dateStr);
  const monthStart = new Date(monthStr + '-01');
  const dayDiff = Math.ceil((date - monthStart) / (1000 * 60 * 60 * 24));
  return dayDiff + 1;
};

/**
 * Check if cursor is hoverable (can drag)
 * @param {number} branchID - Current branch ID
 * @param {number} userRole - User role
 * @param {string} currentMonth - Current month viewing
 * @param {Object} booking - Booking object
 * @returns {boolean}
 */
export const canDragBooking = (branchID, userRole, currentMonth, booking) => {
  if (booking?.move === true) return false;
//   console.log('🔍 canDragBooking check:', {
//     branchID: Number(branchID),
//     userRole: Number(userRole),
//     currentMonth,
//     booking: booking ? { id: booking.id, name: booking.name, checkin: booking.checkin } : null
//   });

  // Only allow for branchID 99
  if (Number(branchID) !== 99) {
    // console.log('❌ Branch check failed:', Number(branchID), '!== 99');
    return false;
  }

  // Only allow role 0 (Reception)
  if (Number(userRole) !== 0) {
    // console.log('❌ Role check failed:', Number(userRole), '!== 0');
    return false;
  }

  // Only allow booking in current month
  const result = isBookingInCurrentMonth(booking, currentMonth);
//   console.log('🔍 Month check result:', result, 'booking.checkin:', booking?.checkin, 'currentMonth:', currentMonth);
  return result;
};

/**
 * Calculate cell visual position and size
 * @param {number} index - Cell index in month
 * @returns {Object} { left: number, width: number }
 */
export const getCellDimensions = (index) => {
  const cellWidth = 80; // pixels per day
  return {
    left: index * cellWidth,
    width: cellWidth
  };
};
